import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { Box, Grid, Typography, styled, useTheme } from '@mui/material'
import { t } from 'i18next'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useUpdatePositionDrawingsOfaMooring } from 'src/api/moorings'
import {
  invalidatePositionDrawingCache,
  useAddPositionDrawing,
  useGetPositionDrawing,
  useGetPositionDrawingInMooring,
  useGetSimplePositionDrawings,
  useUpdatePositionDrawing,
} from 'src/api/position-drawing'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { FlexSpinner } from 'src/components/Spinner'
import { ALREADY_EXISTS } from 'src/constant'
import { IPosition, IPositionType } from 'src/generated-types'
import { IPositionDrawing } from 'src/generated-types/position-drawing'
import { IPositionDrawingProduct } from 'src/generated-types/position-drawing-product'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { usePositionDrawingFilters } from 'src/hooks/usePositionDrawingFilters'
import { ShowDialogProps } from 'src/show-dialog'
import PositionDrawingEmptyStatus2x from '../../../assets/Common 2@2x.png'
import EmptyTemplateImage from '../../../assets/EmptyTemplateImage.svg'
import NoTemplatesAvailable from '../../../assets/noTemplatesAvailable.png'
import { ManageTemplatesDefaultFilter } from './Components/ManageTemplatesDefaultFilter'
import PositionDrawingCard from './Components/PositionDrawingCard'
import { CustomizeComponentContainer } from './CustomizeComponentContainer'
interface ManageTemplatesProps extends ShowDialogProps {
  positionId?: EntityId
  mooringId: EntityId
  positions?: Array<IPosition>
  customerId: EntityId
}


export const ManageTemplatesDialog: React.FC<ManageTemplatesProps> = ({
  pop,
  positionId,
  positions,
  mooringId,
  customerId,
}) => {
  const { t: translate } = useTranslation(['mooring', 'common', 'main-component'])
  const [selectedTemplate, setSelectedTemplate] = useState<IPositionDrawing>()
  const { filters, debouncedSearch, queryCategoryIds, onSearchChange, onProductCategoriesChange } =
    usePositionDrawingFilters()
  const linkedPosition = useMemo(() => (positions || []).find(x => x.id == positionId), [positions, positionId])
  const positionTypeQuery = linkedPosition?.type as IPositionType | undefined
  const { data: positionDrawings, isLoading: defaultTemplatesLoading } = useGetSimplePositionDrawings(
    debouncedSearch,
    positionTypeQuery,
    customerId as number,
    queryCategoryIds,
  )
  const defaultTemplates = positionDrawings ?? []
  const { data: positionDrawing, isLoading: positionDrawingLoading } = useGetPositionDrawing(selectedTemplate?.id ?? 0)
  const { data: selectedPositionDrawings, isLoading: selectedPositionDrawingsLoading } = useGetPositionDrawingInMooring(
    customerId,
    mooringId,
  )
  const [customizedTemplates, setCustomizedTemplates] = useState<IPositionDrawing[]>([])
  const [positionDrawingProductsList, setPositionDrawingProducts] = useState<IPositionDrawingProduct[]>()
  const [positionDrawingProductsListToCompare, setPositionDrawingProductsListToCompare] =
    useState<IPositionDrawingProduct[]>()
  const theme = useTheme()
  const { showWarningDialog } = useDialogCtx()
  const mutation = useUpdatePositionDrawingsOfaMooring(customerId, mooringId)
  const { mutateAsync, error } = useAddPositionDrawing()
  const updatePositionDrawing = useUpdatePositionDrawing(selectedTemplate?.id ?? 0)

  useEffect(() => {
    setSelectedTemplate(undefined)
  }, [debouncedSearch, queryCategoryIds])

  useEffect(() => {
    if (positionDrawing?.positionDrawingProducts != null) {
      const productsToUpdate = positionDrawing.positionDrawingProducts.map(p => ({ ...p }))
      setPositionDrawingProductsListToCompare(productsToUpdate)
      setPositionDrawingProducts(productsToUpdate)
    }
  }, [positionDrawing?.id])

  useEffect(() => {
    const selectedDrawings: IPositionDrawing[] = selectedPositionDrawings?.items as IPositionDrawing[]
    setCustomizedTemplates(pre => {
      const tempDrawings: IPositionDrawing[] = []
      selectedDrawings &&
        selectedDrawings.forEach(e => {
          if (pre.find(c => c.id == e.id) == null) tempDrawings.push(e)
        })
      return [...tempDrawings, ...pre]
    })
  }, [selectedPositionDrawings])

  const handleConfirmDelete = async (id: number) => {
    await showWarningDialog(translate('are-you-sure-to-delete'), translate('are-you-sure-to-delete-message'), true, [
      {
        text: translate('delete', { ns: 'common' }),
        variant: 'contained',
        callback: () => {
          setCustomizedTemplates(prev => prev.filter(drawing => drawing.id !== id))
        },
      },
    ])
  }

  const positionDrawingUpdate = async (
    positionDrawings: IPositionDrawing[],
    initialPositionDrawings?: IPositionDrawing[],
  ) => {
    positionDrawings.forEach(p => {
      p.positionDrawingProducts = p.positionDrawingProducts.filter(x => typeof x.id !== 'string')
    })
    await mutation.mutateAsync({ positionDrawings, initialPositionDrawings }).then(() => {
      if (positionDrawings.length == 0 && initialPositionDrawings?.length == 0) {
        toast(translate('no-changes-to-update-in-selectedtemplates'), { type: 'error' })
      } else {
        invalidatePositionDrawingCache.getPositionDrawingInMooring(customerId, mooringId)
        invalidatePositionDrawingCache.getSimplePositionDrawings()
        invalidatePositionDrawingCache.getPositionDrawingById(selectedTemplate?.id ?? 0)
        toast(t('updatedSuccessfully', { ns: 'common' }), { type: 'success' })
        pop()
      }
    })
  }

  const checkPositionDrawingProductsUpdated = (
    defaultProducts: IPositionDrawingProduct[],
    updatedProducts: IPositionDrawingProduct[],
  ): boolean => {
    return !(JSON.stringify(defaultProducts) === JSON.stringify(updatedProducts))
  }

  useEffect(() => {
    if (error && error['data'] == ALREADY_EXISTS) {
      toast(translate('already-exists'), { type: 'error' })
    }
  }, [error])

  const addDrawing = (newDrawing: IPositionDrawing, positionDrawingProducts: IPositionDrawingProduct[]) => {
    if (!positionDrawingProducts || positionDrawingProducts.length === 0) {
      toast(translate('noProductsAssignedInTheTemplate', { ns: 'mooring' }), { type: 'error' })
      return
    }

    const isProductsUpdated = checkPositionDrawingProductsUpdated(
      positionDrawingProductsListToCompare as IPositionDrawingProduct[],
      positionDrawingProducts as IPositionDrawingProduct[],
    )

    newDrawing.mooringId = mooringId as EntityId

    const isAlreadySelected = customizedTemplates.find(c => c.id === newDrawing.id) != null

    if (isAlreadySelected && isProductsUpdated) {
      const updatedDrawing = { ...newDrawing, positionDrawingProducts }
      updatePositionDrawing.mutateAsync(updatedDrawing).then(() => {
        invalidatePositionDrawingCache.getPositionDrawingInMooring(customerId, mooringId)
        invalidatePositionDrawingCache.getSimplePositionDrawings()
        invalidatePositionDrawingCache.getPositionDrawingById(newDrawing.id)
        setCustomizedTemplates(prev => prev.map(c => (c.id === newDrawing.id ? updatedDrawing : c)))
        toast(translate('templateUpdatedSuccessfully', { ns: 'mooring' }), { type: 'success' })
      })
    } else if (isAlreadySelected) {
      toast(translate('noChangesToUpdate', { ns: 'mooring' }), { type: 'warning' })
    } else {
      newDrawing.positionDrawingProducts = positionDrawingProducts as IPositionDrawingProduct[]
      newDrawing.isCloned = true
      newDrawing.customerId = customerId as number
      mutateAsync(newDrawing as IPositionDrawing).then(() => {
        invalidatePositionDrawingCache.getPositionDrawingInMooring(customerId, mooringId)
        invalidatePositionDrawingCache.getSimplePositionDrawings()
        invalidatePositionDrawingCache.getPositionDrawingById(newDrawing.id)
        toast(translate('templateAddedSuccessfully', { ns: 'mooring' }), { type: 'success' })
      })
    }
  }

  return (
    <GenericDialogLayout
      sx={{ height: '95vh' }}
      title={t('manage-templates', { ns: 'mooring' })}
      pop={pop}
      actions={[
        <MainButton
          key={'cancel'}
          onClick={() => {
            pop(false)
          }}
        >
          {t('cancel', { ns: 'common' })}
        </MainButton>,
        <MainButton
          key={'assignTemplates'}
          onClick={() => positionDrawingUpdate(customizedTemplates, selectedPositionDrawings?.items)}
        >
          {t('updateTemplates', { ns: 'mooring' })}
        </MainButton>,
      ]}
    >
      <Grid container px={1} columnSpacing={2}>
        <Grid item xs={2.5}>
          <SolidBox height={'calc(100vh - 250px)'}>
            <Box padding={'8px'}>
              <Typography fontWeight={'600'} variant="subtitle1">
                {t('default-templates', { ns: 'mooring' })}
              </Typography>
            </Box>
            <ManageTemplatesDefaultFilter
              drawingFilters={filters}
              onSearchChange={onSearchChange}
              onProductCategoriesChange={onProductCategoriesChange}
            />
            <Box padding={'8px'} paddingTop={'0px'} display={'flex'} flexDirection={'column'} flex={1} minHeight={0}>
              <ScrollableBox height={0}>
                {defaultTemplatesLoading ? (
                  <FlexSpinner />
                ) : defaultTemplates.length > 0 ? (
                  defaultTemplates.map(value => {
                    return (
                      <PositionDrawingCard
                        key={value.id}
                        positionDrawing={value}
                        setSelectedTemplate={p => {
                          setSelectedTemplate(p)
                        }}
                        border={`1px solid ${theme.palette.gray70.main}`}
                        isDefaultDrawing={true}
                        toolTipText={''}
                        isSelected={selectedTemplate?.id == value.id}
                      />
                    )
                  })
                ) : (
                  <EmptyStatusCard
                    image={NoTemplatesAvailable}
                    header={translate('no-templates-available-header')}
                    content={translate('no-templates-available-content')}
                    imageWidth={'330px'}
                  />
                )}
              </ScrollableBox>
            </Box>
          </SolidBox>
        </Grid>
        <Grid item xs={7}>
          <Box height={'calc(100vh - 200px)'}>
            <Grid item xs={12}>
              <Box
                height={'300px'}
                boxShadow={selectedTemplate != null ? `0px 3px 6px ${theme.palette.veryDarkTransparentBlack.main}` : ''}
                border={selectedTemplate == null ? `1px solid ${theme.palette.gray70.main}` : ''}
                borderRadius={'8px'}
              >
                {selectedTemplate == null ? (
                  <Box>
                    <EmptyStatusCard
                      image={EmptyTemplateImage}
                      header={translate('no-template-selected-header')}
                      content={translate('no-template-selected-content')}
                      imageWidth={'247px'}
                      height={'300px'}
                      headerFontSize="18px"
                      contentFontSize="14px"
                      contentTopMargin="16px"
                      imageContainerClasses="pb-0"
                      contentClasses="mt-0"
                    />
                  </Box>
                ) : (
                  <img
                    src={positionDrawing?.fileUrl}
                    style={{ maxHeight: '300px', width: '1045px', borderRadius: '4px' }}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box paddingTop={'4px'}>
                <Box
                  bgcolor={theme.palette.white[theme.palette.mode]}
                  height={'calc(100vh - 554px)'}
                  border={`1px solid ${theme.palette.gray70.main}`}
                  borderRadius={'4px'}
                  padding={'2px'}
                >
                  {selectedTemplate == null ? (
                    <EmptyStatusCard
                      image={PositionDrawingEmptyStatus2x}
                      header={translate('no-templates-header')}
                      content={translate('no-templates-content')}
                      imageWidth={'247px'}
                      headerFontSize="18px"
                      contentFontSize="14px"
                      contentTopMargin="16px"
                      imageContainerClasses="pb-3"
                    />
                  ) : (
                    <CustomizeComponentContainer
                      positionDrawing={positionDrawing}
                      setCustomizedTemplates={setCustomizedTemplates}
                      setSelectedTemplate={setSelectedTemplate}
                      addDrawing={addDrawing}
                      initialProducts={positionDrawingProductsList}
                      customizedTemplates={customizedTemplates}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={2.5}>
          <DashedBox height={'calc(100vh - 250px)'}>
            <Box padding={'8px'}>
              <Typography fontWeight={'600'} variant="subtitle1">
                {t('selected-templates', { ns: 'mooring' })}
              </Typography>
            </Box>
            <Box padding={'8px'} display={'flex'} flexDirection={'column'} flex={1} minHeight={0}>
              <ScrollableBox height={0}>
                <Box>
                  {selectedPositionDrawingsLoading ? (
                    <FlexSpinner />
                  ) : selectedPositionDrawings?.items.length == 0 && customizedTemplates?.length == 0 ? (
                    <Box py={8} className="text-center">
                      <EmptyStatusCard
                        image={PositionDrawingEmptyStatus2x}
                        header={translate('no-selected-templates-header')}
                        content={translate('no-selected-templates-content')}
                        imageWidth={'247px'}
                        headerFontSize="18px"
                        contentFontSize="14px"
                      />
                    </Box>
                  ) : (
                    <>
                      {[...customizedTemplates]?.map(value => {
                        return (
                          <PositionDrawingCard
                            key={value.id}
                            setSelectedTemplate={p => {
                              setSelectedTemplate(p)
                            }}
                            positionDrawing={value}
                            border={`1px dashed ${theme.palette.gray70.main}`}
                            icon={<DeleteOutlineRoundedIcon htmlColor={theme.palette.primaryMain[theme.palette.mode]} />}
                            isDefaultDrawing={false}
                            toolTipText={translate('delete', { ns: 'common' })}
                            handleConfirmDelete={handleConfirmDelete}
                          />
                        )
                      })}
                    </>
                  )}
                </Box>
              </ScrollableBox>
            </Box>
          </DashedBox>
        </Grid>
      </Grid>
    </GenericDialogLayout>
  )
}

const SolidBox = styled(Box)`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.palette.gray70.main};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.palette.silent[theme.palette.mode]};
`

const DashedBox = styled(Box)`
  display: flex;
  flex-direction: column;
  border: 2px dashed ${({ theme }) => theme.palette.gray70.main};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.palette.silent[theme.palette.mode]};
`
const ScrollableBox = styled(Box)({
  width: 'auto !important',
  flex: 1,
  borderRadius: '8px',
  overflowY: 'auto',
})
